<template>
  <PopupContainer>
    <template #header>
      <EasyTabsV2 v-if="showCmds" :active-tab="activeTab" class="tabs">
        <el-tab-pane label="字段设置" name="table" />
        <el-tab-pane v-if="showCmds" label="按钮设置" name="cmd" />
      </EasyTabsV2>
      <div v-else>字段设置</div>
    </template>

    <div class="tbl-cfg-drawer">
      <div class="tbl-cfg-body">
        <TableColumnConfig v-if="activeTab.table" :table-ctx="tableCtx" />
        <CmdLayoutConfig
          v-if="activeTab.cmd && showCmds"
          :setting="cmdbarApi!"
          :visibles="cmdMenuVisibles"
        />
      </div>

      <div class="tbl-cfg-footer">
        <el-checkbox
          v-model="checkAll"
          :indeterminate="isIndeterminate"
          @change="handleCheckAllChange as any"
        >
          全选
        </el-checkbox>
        <div>
          <el-button @click="restoreDefault">恢复默认</el-button>
          <el-button type="primary" @click="popup.done(true)">确定</el-button>
        </div>
      </div>
    </div>
  </PopupContainer>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from 'vue';
import { TableCtx, CmdbarApi } from '../types';
import { usePopup, PopupCtx } from '../../core/EasyPopup';
import { ActiveTabs } from '../../core/types';
import PopupContainer from '../popup/PopupContainer.vue';
import EasyTabsV2 from '../EasyTabsV2.vue';
import TableColumnConfig from './TableColumnConfig.vue';
import CmdLayoutConfig from './CmdLayoutConfig.vue';

const props = defineProps<{
  showCmdConfig?: boolean; // 是否显示命令配置
  tableCtx: TableCtx;
  cmdbarApi: CmdbarApi | undefined;

  popupCtx?: PopupCtx<true>;
}>();
const popup = usePopup(props);

// 是否有菜单按钮
const showCmds =
  props.showCmdConfig &&
  props.cmdbarApi &&
  props.cmdbarApi.cmdLabelMap().length > 0;

// 当前tab
const activeTab = reactive<ActiveTabs>({
  table: true,
  cmd: false
});

const cmdMenuVisibles = reactive<Record<string, boolean>>({});
const tableColumnVisibles = ref<Record<string, boolean | 'deleted'>>({});
watch(
  () => props.tableCtx.visibles,
  (v) => {
    tableColumnVisibles.value = v;
  },
  { deep: true, immediate: true }
);

const visibleItems = () => {
  if (activeTab.table) {
    return tableColumnVisibles.value;
  }

  return cmdMenuVisibles;
};

/// 全选等操作
const checkAll = computed({
  // 是否全选
  get: () => !Object.values(visibleItems()).includes(false),
  set: (val) => val
});

const isIndeterminate = computed({
  // 是否有选择项
  get: () => {
    const values = Object.values(visibleItems());
    return values.includes(true) && values.includes(false);
  },
  set: (val) => val
});

function showItem(name: string, visible: boolean, save = true) {
  if (!name) {
    if (activeTab.table) {
      props.tableCtx.showColumn?.(name, visible, save);
    } else {
      props.cmdbarApi?.setVisible?.(name, visible, save);
    }
    return;
  }

  if (activeTab.table) {
    props.tableCtx.showColumn?.(name, visible, save);
  } else {
    cmdMenuVisibles[name] = visible;
    props.cmdbarApi?.setVisible?.(name, visible, save);
  }
}

const handleCheckAllChange = (show: boolean) => {
  for (let name in visibleItems()) {
    showItem(name, show, false); // 不立即保存
  }

  showItem('', false);
};

const restoreDefault = () => {
  if (activeTab.table) {
    props.tableCtx.restore?.();
  } else {
    props.cmdbarApi?.restore?.();
  }

  popup.done(true);
};
</script>

<style lang="scss" scoped>
// .drawer-header-title {
//   font-weight: 600;
//   color: #130f3c;
// }

.tbl-cfg-drawer {
  --tabbed-drawer-padding: 16px;

  display: flex;
  flex-direction: column;
  height: 100%;

  :deep(.el-drawer__body) {
    padding: 0;
    overflow: hidden;
  }
}

.tbl-cfg-body {
  flex-grow: 1;
  overflow: hidden !important;
  display: flex;
  flex-direction: column;
}

.tbl-cfg-footer {
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #ccc8;
  padding: 16px;

  .el-checkbox__input {
    padding-top: 2px;
  }

  .el-button {
    height: 32px;
    border: var(--el-border);
    border-color: var(--el-button-border-color);
  }
}

.tabs {
  :deep(.el-tabs__header) {
    padding-left: 0 !important;
  }

  :deep(.el-tabs__nav-wrap) {
    padding: 0 var(--tabbed-drawer-padding);
    padding-top: 4px;
  }

  :deep(.el-tabs__nav-wrap::after) {
    display: none;
  }

  :deep(.el-tabs__active-bar) {
    display: none;
  }

  :deep(.el-tabs__nav.is-top) {
    transform: translate(0px, 4px) !important;
  }
}
</style>
